import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Page from "../../components/Common/Page";

// Course Types Components
import {
  Filter as CourseTypeFilter,
  FloatRight as CourseTypeFloatRight,
  ListView as CourseTypeListView,
} from "../../components/Coursetypes";
import { getCoursetypesList } from "../../appRedux/actions/coursetypes";

// Tags Components
import {
  Filter as TagFilter,
  FloatRight as TagFloatRight,
  ListView as TagListView,
  GridView as TagGridView,
  ScopeTabs as TagScopeTabs,
} from "../../components/Tags";
import { getTagsList } from "../../appRedux/actions/tags";

const CoursetypeList = ({ defaultTab }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine initial active tab based on route or prop
  const isTagsRoute = location.pathname.includes("/tags");
  const [activeTab, setActiveTab] = useState(
    defaultTab || (isTagsRoute ? "tags" : "tags")
  );

  // Course Types state
  const [ctSearchText, setCtSearchText] = useState("");
  const [ctViewStatus, setCtViewStatus] = useState("listView");

  // Tags state
  const [tagSearchText, setTagSearchText] = useState("");
  const [tagViewStatus, setTagViewStatus] = useState("listView");

  useEffect(() => {
    dispatch(getCoursetypesList({ page: 1, size: 10 }));
    dispatch(getTagsList({ page: 1, size: 10 }));
  }, [dispatch]);

  // Update tab state when route changes
  useEffect(() => {
    if (location.pathname.includes("/tags")) {
      setActiveTab("tags");
    } else if (location.pathname.includes("/coursetype") && !defaultTab) {
      setActiveTab("coursetype");
    }
  }, [location.pathname, defaultTab]);

  const handleTabSwitch = (tabKey) => {
    setActiveTab(tabKey);
    if (tabKey === "tags") {
      navigate("/tags/list", { replace: true });
    } else {
      navigate("/coursetype/list", { replace: true });
    }
  };

  return (
    <Page title={activeTab === "tags" ? "Tags Management" : "Course Types"}>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Clean Standard Header */}
          <div className="page-header mb-4">
            <div className="row align-items-center">
              <div className="col-md-5 col-sm-12">
                <h3 className="page-title mb-1 fw-bold">
                  {activeTab === "tags" ? "Tags & Classification" : "Course Types Management"}
                </h3>
                <ul className="breadcrumb bg-transparent p-0 m-0 text-muted small">
                  <li className="breadcrumb-item"><a href="/">Dashboard</a></li>
                  <li className="breadcrumb-item active">
                    {activeTab === "tags" ? "Tags" : "Course Types"}
                  </li>
                </ul>
              </div>

              {/* Center Sub Navigation Switcher */}
              <div className="col-md-4 col-sm-12 text-center my-2 my-md-0">
                <div className="d-inline-flex align-items-center gap-1 bg-light p-1 rounded-pill border shadow-sm">
                  <button
                    type="button"
                    onClick={() => handleTabSwitch("tags")}
                    className={`btn btn-sm rounded-pill px-3 py-1 d-flex align-items-center gap-2 ${
                      activeTab === "tags"
                        ? "btn-primary shadow-sm fw-bold"
                        : "btn-light text-secondary border-0"
                    }`}
                  >
                    <i className="la la-tags" />
                    <span>Tags</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabSwitch("coursetype")}
                    className={`btn btn-sm rounded-pill px-3 py-1 d-flex align-items-center gap-2 ${
                      activeTab === "coursetype"
                        ? "btn-primary shadow-sm fw-bold"
                        : "btn-light text-secondary border-0"
                    }`}
                  >
                    <i className="la la-pencil-ruler" />
                    <span>Course Types</span>
                  </button>
                </div>
              </div>

              {/* Right Action Controls */}
              <div className="col-md-3 col-sm-12 text-md-end text-sm-start col-auto float-right ml-auto">
                {activeTab === "tags" ? (
                  <TagFloatRight
                    viewStatus={tagViewStatus}
                    onChange={(status) => setTagViewStatus(status)}
                  />
                ) : (
                  <CourseTypeFloatRight
                    viewStatus={ctViewStatus}
                    onChange={(status) => setCtViewStatus(status)}
                  />
                )}
              </div>
            </div>
          </div>

          {/* TAB 1: TAGS MANAGEMENT */}
          {activeTab === "tags" && (
            <>
              {/* Scope Bifurcation Filter Bar */}
              <TagScopeTabs />

              {/* Tags Search Filter */}
              <TagFilter searchText={tagSearchText} setSearchText={setTagSearchText} />

              {/* Tags View (List or Grid) */}
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  {tagViewStatus === "listView" ? (
                    <TagListView searchText={tagSearchText} />
                  ) : (
                    <TagGridView searchText={tagSearchText} />
                  )}
                </div>
              </div>
            </>
          )}

          {/* TAB 2: COURSE TYPES MANAGEMENT */}
          {activeTab === "coursetype" && (
            <>
              <CourseTypeFilter
                initialFetchComplete={true}
                searchText={ctSearchText}
                setSearchText={setCtSearchText}
              />
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <CourseTypeListView searchText={ctSearchText} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Page>
  );
};

export default CoursetypeList;
